/* eslint-disable react-hooks/rules-of-hooks */
import { Ritn } from '.generated/templates/Project.Ritn.model';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { getEnumValue } from 'lib/util/enum';
import { classnames } from 'tailwindcss-classnames';
import CenterMap, { CenterMapProps } from './CenterMap';
import ReactPaginate from 'react-paginate';
import { useCallback, useEffect, useState } from 'react';
import TableHeading from './Icon/TableHeading';

type ParticipationCenterEntry = Ritn.ParticipationCenter.Fields.ParticipationCenter & {
  id: string;
};

export type ParticipatingCenterProps = {
  fields?: {
    root?: Ritn.ParticipationCenter.Fields.ParticipationCenterTable & {
      children?: ParticipationCenterEntry[];
    };
  };
};

/**
 * Sort comparison functions for centers by state.
 */
const centerSorter = {
  byState: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    getEnumValue(a?.fields?.state) > getEnumValue(b?.fields?.state) ? 1 : -1,
  byName: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    (a?.fields?.displayName.value as string) > (b?.fields?.displayName.value as string) ? 1 : -1,
  byProgramType: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    getEnumValue(a?.fields?.adultPediatric) > getEnumValue(b?.fields?.adultPediatric) ? 1 : -1,
  byTraumaCenter: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    getEnumValue(a?.fields?.traumaCenter) > getEnumValue(b?.fields?.traumaCenter) ? 1 : -1,
  byBurnCenter: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    getEnumValue(a?.fields?.burnCenter) > getEnumValue(b?.fields?.burnCenter) ? 1 : -1,
  byNdmsHospital: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    getEnumValue(a?.fields?.ndmsHospital) > getEnumValue(b?.fields?.ndmsHospital) ? 1 : -1,
  byHppHospital: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    getEnumValue(a?.fields?.hppHospital) > getEnumValue(b?.fields?.hppHospital) ? 1 : -1,
  byCity: (a: ParticipationCenterEntry, b: ParticipationCenterEntry) =>
    (a?.fields?.city.value as string) > (b?.fields?.city.value as string) ? 1 : -1,
};
/**
 * Returns a center sort function for a given column name
 * @param fields Participating center fields
 * @param column Name of the column to get sorter for
 */
const getCenterSorterForColumn = (
  column: string,
  root?: Ritn.ParticipationCenter.Fields.ParticipationCenterTable
) => {
  const labels = root?.fields;
  if (column === labels?.stateLabel.value) {
    return centerSorter.byState;
  }
  if (column === labels?.centerLabel.value) {
    return centerSorter.byName;
  }
  if (column === labels?.adultPediatricLabel.value) {
    return centerSorter.byProgramType;
  }
  if (column === labels?.traumaCenterLabel.value) {
    return centerSorter.byTraumaCenter;
  }
  if (column === labels?.burnCenterLabel.value) {
    return centerSorter.byBurnCenter;
  }
  if (column === labels?.ndmsHospitalLabel.value) {
    return centerSorter.byNdmsHospital;
  }
  if (column === labels?.hppHospitalLabel.value) {
    return centerSorter.byHppHospital;
  }
  if (column === labels?.cityLabel.value) {
    return centerSorter.byCity;
  }
  return undefined;
};

/**
 * Retrieves label for a given column.
 * @param fields Participating Center fields.
 * @param enumval Enum value identifying column.
 */
const columnLabel = (
  enumval: string,
  root?: Ritn.ParticipationCenter.Fields.ParticipationCenterTable
): string => {
  const labels = root?.fields;
  switch (enumval) {
    case 'centername':
      return labels?.centerLabel.value || enumval;
    case 'adultpediatric':
      return labels?.adultPediatricLabel.value || enumval;
    case 'burncenter':
      return labels?.burnCenterLabel.value || enumval;
    case 'city':
      return labels?.cityLabel.value || enumval;
    case 'hpphospital':
      return labels?.hppHospitalLabel.value || enumval;
    case 'ndmshospital':
      return labels?.ndmsHospitalLabel.value || enumval;
    case 'state':
      return labels?.stateLabel.value || enumval;
    case 'traumacenter':
      return labels?.traumaCenterLabel.value || enumval;
    default:
      return enumval;
  }
};

const tdClass = classnames('p-2.5', 'border', 'border-gray-quartz');
const trClass = classnames(
  'p-2.5',
  'even:bg-gray-whisper',
  'mb-2',
  'sm:mb-0',
  'border',
  'border-gray-quartz'
);
const thClass = classnames('p-2.5', 'border-b-2', 'border-gray-manatee');

const ParticipatingCenter = ({ fields }: ParticipatingCenterProps): JSX.Element => {
  if (!fields) {
    return <MissingDataSource />;
  }
  const lstCenters = (fields?.root?.children as ParticipationCenterEntry[]).sort(
    centerSorter.byState
  );
  const labels = fields.root?.fields;
  const [tableData, setTableData] = useState<ParticipationCenterEntry[]>(lstCenters);
  const [currentItems, setCurrentItems] = useState<ParticipationCenterEntry[]>(lstCenters);
  const [sortIndicator, setIndicator] = useState({
    columName: labels?.stateLabel.value as string,
    isAscending: false,
  });
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);

  const pageSize = labels?.paginationSize.value ? labels?.paginationSize.value : 10;
  const displayPagination = lstCenters.length > pageSize;

  useEffect(() => {
    const endOffset = itemOffset + pageSize;
    const copyOfprevCenterList = [...tableData];
    setCurrentItems(copyOfprevCenterList.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(copyOfprevCenterList.length / pageSize));
  }, [itemOffset, tableData, pageSize]);

  const mapFields = { mapfields: fields } as CenterMapProps;
  // Pager dependency does not define event type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePageClick = (event: any) => {
    const newOffset = (event.selected * pageSize) % lstCenters.length;
    setItemOffset(newOffset);
  };

  const sortTable = useCallback((column: string, isAscending: boolean) => {
    setTableData((prevCenterList) => {
      const sortedCenterList = [...prevCenterList];
      const sortFn = getCenterSorterForColumn(column, fields.root);
      if (sortFn) {
        sortedCenterList.sort(sortFn);
      }
      setIndicator({
        ...sortIndicator,
        columName: column,
        isAscending: isAscending ? true : false,
      });
      return isAscending ? sortedCenterList.reverse() : sortedCenterList;
    });
    return 1;
    // Labels are deployed with Sitecore and do not need to be dynamically updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const allCenters = currentItems?.map((item) => {
    return (
      <Tr key={item.id} className={trClass}>
        {fields.root?.fields?.selectColumns.map((column) => {
          const columnvalue = (enumval: string) => {
            switch (enumval) {
              case 'centername':
                return item.fields?.displayName.value;
              case 'adultpediatric':
                return getEnumValue(item.fields?.adultPediatric);
              case 'burncenter':
                return getEnumValue(item.fields?.burnCenter);
              case 'city':
                return item?.fields?.city.value;
              case 'hpphospital':
                return getEnumValue(item.fields?.hppHospital);
              case 'ndmshospital':
                return getEnumValue(item.fields?.ndmsHospital);
              case 'state':
                return getEnumValue(item.fields?.state);
              case 'traumacenter':
                return getEnumValue(item.fields?.traumaCenter);
              default:
                return '';
            }
          };
          return (
            <Td key={column.id} className={tdClass}>
              {columnvalue(getEnumValue(column))}
            </Td>
          );
        })}
      </Tr>
    );
  });
  const tableHeaders = fields?.root?.fields?.selectColumns.map((item) => {
    return (
      <Th key={item.id} className={thClass}>
        <TableHeading
          label={columnLabel(getEnumValue(item), fields.root)}
          sortTable={sortTable}
          sortIndicator={sortIndicator}
          showSortIcon
        />
      </Th>
    );
  });
  return (
    <div data-component="authorable/Ritin/participatingcenter">
      {labels?.showMap.value ? (
        <div className={classnames('md:mx-12', 'lg:mx-24')}>
          <CenterMap {...mapFields} />
        </div>
      ) : null}
      <div className={classnames('sm:hidden')}>
        <div className={classnames('mb-4', 'flex', 'items-center', 'mb-4')}>
          <div className={classnames('text-blue-navy', 'heading-sm', 'mr-6')}>
            {labels?.sortLabel.value}
          </div>
          <select
            className={classnames('border', 'border-gray-spunPearl', 'rounded-md', 'p-2')}
            onChange={(e) => sortTable(e.target.value, false)}
          >
            <option hidden value="">
              Select
            </option>

            {fields.root?.fields?.selectColumns.map((column) => {
              const theColumnLabel = columnLabel(getEnumValue(column), fields.root);
              return (
                <option key={column.id} value={theColumnLabel}>
                  {theColumnLabel}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <Table>
        <Thead>
          <Tr className={classnames('text-left', 'font-bold')}>{tableHeaders}</Tr>
        </Thead>
        <Tbody>{allCenters}</Tbody>
      </Table>
      {displayPagination && (
        <div className={classnames('mt-3')}>
          <ReactPaginate
            nextLabel=""
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={pageCount}
            previousLabel=""
            pageClassName="pagination-item text-blue-navy text-body"
            pageLinkClassName="pagination-link"
            previousClassName="page-item"
            previousLinkClassName="pagination-prev pagination-link"
            nextClassName="page-item"
            nextLinkClassName="pagination-next pagination-link"
            breakLabel="..."
            breakClassName="pagination-item"
            breakLinkClassName="pagination-link"
            containerClassName="pagination-list"
            activeClassName="selected"
          />
        </div>
      )}
    </div>
  );
};

export default ParticipatingCenter;
